import { useEffect, useState, type FormEvent, type ReactNode } from 'react'
import {
  AUTHORITY_EXPIRY_MAX_MS,
  AUTHORITY_STATES,
  type Role,
  type SourceAuthorityMetadata,
} from '@agentwiki/shared'
import {
  sourceAuthorityErrorMessage,
  useSourceAuthority,
  useUpdateSourceAuthority,
} from '../../hooks/use-source-authority'
import { useAuthorityExpiryRefresh } from '../../hooks/use-authority-expiry-refresh'
import { KNOWLEDGE_PRESENTATION_TERMS } from '../../lib/document-result-presentation'
import { deriveEffectiveAuthorityState } from '../../lib/source-authority-metadata'
import { cn } from '../../lib/utils'

const labels = KNOWLEDGE_PRESENTATION_TERMS.authority

function formatDate(value: number | null) {
  if (value === null) return 'No expiry'
  const date = new Date(value)
  return Number.isFinite(value) && !Number.isNaN(date.getTime())
    ? date.toLocaleString()
    : 'Invalid expiry'
}

function inputDate(value: number | null) {
  if (value === null || !Number.isFinite(value) || value > AUTHORITY_EXPIRY_MAX_MS) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60_000)
  try {
    return local.toISOString().slice(0, 16)
  } catch {
    return ''
  }
}

function AuthoritySurface({
  variant,
  documentTitle,
  children,
}: {
  variant: 'reader' | 'library'
  documentTitle: string
  children: ReactNode
}) {
  if (variant === 'library') {
    return (
      <details className="aw-authority-review aw-authority-review-compact">
        <summary aria-label={`Review authority for ${documentTitle}`}>Review authority</summary>
        {children}
      </details>
    )
  }
  return (
    <section className="aw-authority-review" aria-labelledby="source-authority-title">
      <span className="aw-label">Human review</span>
      <h2 id="source-authority-title">Source authority review</h2>
      <p>
        Authority describes whether this source guidance should govern current work. It does not
        change source sync health.
      </p>
      {children}
    </section>
  )
}

export function SourceAuthorityReview({
  role,
  documentId,
  documentTitle,
  sourceId,
  initialMetadata,
  variant = 'reader',
}: {
  role: Role | null
  documentId: string
  documentTitle: string
  sourceId: string
  initialMetadata: SourceAuthorityMetadata
  variant?: 'reader' | 'library'
}) {
  const human = role === 'admin' || role === 'editor' || role === 'viewer'
  const canUpdate = role === 'admin' || role === 'editor'
  const authority = useSourceAuthority(sourceId, human && variant === 'reader', initialMetadata)
  const update = useUpdateSourceAuthority(sourceId, documentId)
  const current = authority.data ?? initialMetadata
  const [state, setState] = useState(current.authorityState)
  const [expiry, setExpiry] = useState(inputDate(current.authorityExpiresAt))
  useAuthorityExpiryRefresh([current.authorityExpiresAt])
  const effectiveState = deriveEffectiveAuthorityState(
    current.authorityState,
    current.authorityExpiresAt,
  )

  useEffect(() => {
    setState(current.authorityState)
    setExpiry(inputDate(current.authorityExpiresAt))
  }, [current.authorityExpiresAt, current.authorityState])

  if (!human || !sourceId) return null

  const error = authority.error ?? update.error
  const controlsRevoked = update.error?.status === 403 || update.error?.status === 404
  const save = async (event: FormEvent) => {
    event.preventDefault()
    const expiresAt = expiry ? new Date(expiry).getTime() : null
    if (expiry && !Number.isFinite(expiresAt)) return
    await update
      .mutateAsync({ state, expiresAt, expectedReviewedAt: current.authorityReviewedAt })
      .catch(() => undefined)
  }

  return (
    <AuthoritySurface variant={variant} documentTitle={documentTitle}>
      <div className="aw-authority-lifecycle" aria-label="Authority lifecycle">
        <span>Stored {labels[current.authorityState].label}</span>
        <span className={cn(effectiveState === 'expired' && 'is-expired')}>
          Effective {labels[effectiveState].label}
        </span>
        <span>{formatDate(current.authorityExpiresAt)}</span>
      </div>

      {canUpdate && !authority.error && !controlsRevoked && (
        <form className="aw-authority-form" onSubmit={(event) => void save(event)}>
          <label>
            <span>Stored authority</span>
            <select
              name="authority-state"
              value={state}
              onChange={(event) => setState(event.target.value as typeof state)}
            >
              {AUTHORITY_STATES.map((value) => (
                <option key={value} value={value}>
                  {labels[value].label}
                </option>
              ))}
            </select>
          </label>
          <label>
            <span>Valid until (optional)</span>
            <input
              name="authority-expiry"
              type="datetime-local"
              max="9999-12-31T23:59"
              value={expiry}
              onChange={(event) => setExpiry(event.target.value)}
            />
          </label>
          <div className="aw-authority-actions">
            {expiry && (
              <button type="button" onClick={() => setExpiry('')}>
                Clear expiry
              </button>
            )}
            <button type="submit" disabled={update.isPending}>
              {update.isPending ? 'Saving review…' : 'Save authority review'}
            </button>
          </div>
        </form>
      )}

      {update.isSuccess && (
        <p className="aw-authority-message is-success" role="status">
          Authority review saved.
        </p>
      )}
      {error && (
        <p className="aw-authority-message is-error" role="alert">
          {sourceAuthorityErrorMessage(error)}
        </p>
      )}
    </AuthoritySurface>
  )
}
